import 'firebase/compat/auth';
import { v4 as uuid } from 'uuid';

import { Typography } from '@mui/material';
import { isSomething } from '../../../utils/utils';
import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, startOfWeek, addWeeks, subWeeks, isBefore } from 'date-fns';

//src
import { dataColumns } from './columns';
import {
  getCurrentWeek,
  getDeliverablesList,
  getNextWeek,
  getPreviousWeek,
  getRolesList,
  initializeWeeklyHours,
  fetchWeeklyHoursMap,
  saveWeeklyHoursMutation,
  toggleStarredProject,
} from './time-sheet.utils';
import Table from '../../../components/table/table';
import { TimeSheetStyled } from './time-sheet.styles';
import { Button } from '../../../components/button/button';
import useCompanies from '../../../hooks/useCompanies';
import { fetchProjects } from '../../../services/projectsServices';
import { convertMinutesToRoundedDecimalHours } from '../../../utils/utils';
import TimesheetNotesModal from './timesheet-notes/timesheet-notes';
import { addNotification } from '../../../store/slices/ui-slice';
import { useAppDispatch } from '../../../store/hooks';

const TimeSheet = ({ selectedUser, currentUser, userPermissions, allUsers }) => {
  const dispatch = useAppDispatch();
  const [currentWeek, setCurrentWeek] = useState(getCurrentWeek());
  const [tableData, setTableData] = useState([]);
  const [resetKey, setResetKey] = useState(0);
  const [timesheetModalData, setTimesheetModalData] = useState(null);
  const isAdminUser = userPermissions === 'Admin';
  const isSupervisor = userPermissions === 'Supervisor';
  const isProjectManager = userPermissions === 'Project Manager';

  const filteredUsers = allUsers.filter((user) => !['Inactive'].includes(user.employmentStatus));
  const userToSaveFor = filteredUsers.find(
    (user) => user.email === (selectedUser?.email || currentUser.email),
  );

  const { companies } = useCompanies();

  const { data: projects, isLoading: isProjectLoading } = useQuery({
    queryKey: ['projects-list'],
    queryFn: () => fetchProjects(),
  });

  const { data: projectsList, isLoading: isProjectsLoading } = useQuery({
    queryKey: ['filteredProjectsListedProjects'],
    queryFn: async () => {
      const allProjects = await fetchProjects();
      return allProjects.filter((project) => {
        const isAllowedState =
          ['Opportunity', 'Active'].includes(project.state) ||
          // need to ask that do we need this logic here or need to add on the specific point where I have to show the sheets
          (project.state === 'Committed' && project.isNonBillable);

        const isNotMaterialsPlanner = project.plannerType !== 'Materials';

        return isAllowedState && isNotMaterialsPlanner;
      });
    },
    enabled: true,
  });

  const { data, isLoading: isHoursLoading } = useQuery({
    queryKey: [`weeklyHoursMap${currentWeek}`, currentWeek, userToSaveFor],
    queryFn: () => fetchWeeklyHoursMap(userToSaveFor, currentWeek, projects),
    enabled: projects?.length > 0,
    staleTime: 0,
    gcTime: 0,
  });

  const { weeklyHours = [], starredProjects: fetchedStarredProjects = [] } = data || {};

  // Initialize starredProjects state with fetched data
  const [starredProjects, setStarredProjects] = useState(fetchedStarredProjects);

  const handleStarUpdate = (updatedProjects) => {
    setStarredProjects(updatedProjects);
  };
  useEffect(() => {
    if (data?.starredProjects) {
      setStarredProjects(data.starredProjects);
    }
  }, [data]);

  const sortedProjectsList = useMemo(() => {
    return (projectsList || []).sort((a, b) => {
      const aIsStarred = starredProjects.includes(a.id);
      const bIsStarred = starredProjects.includes(b.id);
      return bIsStarred - aIsStarred;
    });
  }, [projectsList, starredProjects]);

  const { mutate, isPending } = useMutation({ mutationFn: saveWeeklyHoursMutation });

  useEffect(() => {
    if (!isProjectsLoading && !isHoursLoading) {
      const projectMap = new Map(projectsList.map((p) => [p.id, p]));

      setTableData(
        (weeklyHours || []).map((item) => {
          const projectDetails = projectMap.get(item.id);
          return {
            ...item,
            addToExistingProject: projectDetails?.addToExistingProject,
            isNonBillable: projectDetails?.isNonBillable,
            state: projectDetails?.state,
            deliverables: projectDetails?.deliverables,
            resources: projectDetails?.resources,
          };
        }),
      );
    }
  }, [weeklyHours, projectsList, isProjectsLoading, isHoursLoading]);

  const handlePreviousWeek = () => {
    const firstDate = currentWeek[0];
    const previousWeekStart = getPreviousWeek(firstDate)[0];
    const startOfCurrentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
    const fiveWeeksAgo = subWeeks(startOfCurrentWeek, 5);

    if (
      !isAdminUser &&
      !isSupervisor &&
      !isProjectManager &&
      isBefore(previousWeekStart, fiveWeeksAgo)
    ) {
      alert("We're sorry. You can't track time further than 5 weeks back.");
      return;
    }

    const previousWeekDates = getPreviousWeek(firstDate).map((day) => format(day, 'dd-MM-yyyy'));

    const filteredTableData = tableData
      .map((item) => ({
        ...item,
        weeklyHours: item.weeklyHours.filter((day) => previousWeekDates.includes(day.date)),
      }))
      .filter((item) => item.weeklyHours.length > 0);

    setCurrentWeek(getPreviousWeek(firstDate));
    setTableData(filteredTableData);
  };

  const handleNextWeek = () => {
    const firstDate = currentWeek[0];
    const nextWeekStart = getNextWeek(firstDate)[0];
    const weekAfterNextStart = addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), 1);

    if (!isBefore(nextWeekStart, weekAfterNextStart)) {
      alert("We're sorry. You can't track time in the future.");
      return;
    }

    const nextWeekDates = getNextWeek(firstDate).map((day) => format(day, 'dd-MM-yyyy'));

    const filteredTableData = tableData
      .map((item) => ({
        ...item,
        weeklyHours: item.weeklyHours.filter((day) => nextWeekDates.includes(day.date)),
      }))
      .filter((item) => item.weeklyHours.length > 0);

    setCurrentWeek(getNextWeek(firstDate));
    setTableData(filteredTableData);
  };

  // Week range display
  const weekRange = `${format(currentWeek[0], 'MMM dd')} - ${format(
    currentWeek[currentWeek.length - 1],
    'MMM dd',
  )}, ${format(currentWeek[0], 'YYY')}`;

  const handleUpdateWeeklyHours = (uniqueId, date, minutes, note = null) => {
    const updatedTableData = tableData.map((project) => {
      const existingDayData = project.weeklyHours.find((day) => day.date === date);

      return project.uniqueId === uniqueId
        ? {
            ...project,
            weeklyHours: project.weeklyHours.map((day) =>
              day.date === date
                ? {
                    ...day,
                    minutes,
                    note: note !== null ? note : existingDayData.note,
                  }
                : day,
            ),
          }
        : project;
    });

    saveWeeklyHours(updatedTableData);
    setTableData(updatedTableData);
  };

  const handleUpdateData = (uniqueId, key, value) => {
    const projectBeingUpdated = tableData.find((project) => project.uniqueId === uniqueId);
    if (!projectBeingUpdated) return;

    let deliverableId = '';

    const isDeliverableKey = key === 'deliverable';
    if (isDeliverableKey) {
      deliverableId = projectBeingUpdated?.deliverables?.find((x) => x?.name === value)?.id;
    }

    // Prepare the potential updated project for duplicate check
    const updatedProject = {
      ...projectBeingUpdated,
      [key]: value,
      ...(isDeliverableKey ? { deliverableId } : {}),
    };

    // Check for duplicates among existing projects
    const isDuplicate = tableData.some(
      (project) =>
        project.uniqueId !== uniqueId && // Ensure it's not checking against itself
        project.projectName === updatedProject.projectName &&
        project.deliverable === updatedProject.deliverable &&
        project.role === updatedProject.role,
    );

    // If a duplicate is found, alert and return without updating
    if (isDuplicate) {
      alert('A Project with the same Deliverable, and Role already exists.');
      return;
    }

    // Proceed with updating if no duplicates are found
    const updatedTableData = tableData.map((project) =>
      project.uniqueId === uniqueId ? updatedProject : project,
    );

    saveWeeklyHours(updatedTableData);
    setTableData(updatedTableData);
  };

  const handleRemoveProject = (uniqueId) => {
    if (window.confirm('Are you sure you want to delete this project entry?')) {
      const updatedTableData = tableData.filter((project) => project.uniqueId !== uniqueId);
      saveWeeklyHours(updatedTableData);
      setTableData(updatedTableData);
    }
  };

  const handleAddProject = (selection) => {
    if (isSomething(selection) && !tableData.some((x) => x.projectId === selection.id)) {
      const uniqueEntryId = `${selection.id}-${new Date().getTime()}`; // Unique ID
      const newProjectEntry = {
        ...selection,
        uniqueId: uniqueEntryId, // Assign the unique ID
        projectName: selection.projectName,
        deliverable: '', // Initially, no deliverable is selected
        role: '',
        weeklyHours: initializeWeeklyHours(currentWeek),
        deliverablesList: getDeliverablesList(selection),
        rolesList: getRolesList(selection),
      };

      // Insert the new project at the beginning of the tableData array
      const dataToUpdate = [newProjectEntry, ...tableData];

      saveWeeklyHours(dataToUpdate);
      setTableData(dataToUpdate);
    }
    setResetKey((prevKey) => prevKey + 1);
  };

  const calculateTotalMinutesPerColumn = (tableData, timeColumnsList) => {
    return timeColumnsList.map((timeColumn) => {
      const totalMinutesForColumn = tableData.reduce((acc, rowData) => {
        const dayData = rowData.weeklyHours.find((day) => day.date === timeColumn);
        return acc + (dayData ? dayData.minutes : 0);
      }, 0);
      return { timeColumn, totalMinutes: totalMinutesForColumn };
    });
  };

  const timeColumnTotals = calculateTotalMinutesPerColumn(
    tableData,
    currentWeek.map((day) => format(day, 'dd-MM-yyyy')),
  );

  // Calculate total hours for each project
  const calculateProjectTotalHours = (weeklyHours) => {
    const totalMinutes = weeklyHours.reduce((acc, current) => acc + current.minutes, 0);
    return convertMinutesToRoundedDecimalHours(totalMinutes);
  };

  const calculateTotalWeekMinutes = (data) => {
    return data.reduce((total, project) => {
      const projectTotal = project.weeklyHours.reduce((projectSum, day) => {
        return projectSum + day.minutes;
      }, 0);
      return total + projectTotal;
    }, 0);
  };

  // Calculate the total hours for all projects in the week
  const totalWeekHours = convertMinutesToRoundedDecimalHours(calculateTotalWeekMinutes(tableData));

  const saveWeeklyHours = async (dataToUpdate) => {
    mutate(
      {
        user: userToSaveFor,
        currentWeek,
        tableData: dataToUpdate,
        projects,
      },
      {
        onSuccess: () => {
          console.log('data saved');
        },
        onSettled: () => {
          console.log('data settled');
        },
        onError: (error) => {
          dispatch(
            addNotification({
              id: uuid(),
              type: 'error',
              message: `An error occurred while saving please refresh the page and try again: ${error?.message || 'Unknown error'}`,
            }),
          );
        },
      },
    );
  };

  const onTodayClick = () => {
    setCurrentWeek(getCurrentWeek());
  };

  return (
    <TimeSheetStyled>
      <div className="header">
        <div className="date-wrap">
          <div className="left">
            <div className="container">
              <Typography variant="body2" sx={{ fontWeight: '500' }}>
                {weekRange}
              </Typography>
            </div>
          </div>
          <div className="right">
            <div className="container" onClick={onTodayClick}>
              <Typography variant="body2" sx={{ fontWeight: '500' }}>
                Today
              </Typography>
            </div>
          </div>
        </div>
        <div
          className="buttons-wrap"
          style={{ pointerEvents: isPending ? 'none' : 'all', opacity: isPending ? 0.5 : 1 }}
        >
          <Button onClick={handlePreviousWeek} className="left">
            <ChevronLeftIcon />
            Previous
          </Button>
          <Button onClick={handleNextWeek} className="right">
            Next
            <ChevronRightIcon />
          </Button>
        </div>
      </div>

      <div className="table-wrap">
        {isSomething(timesheetModalData) && (
          <Table
            noDataMessage={'No time logged for this time frame.'}
            hasFooter={isSomething(tableData)}
            // data={[getInitialTableData(currentWeek), ...tableData]}
            data={[...tableData]}
            columns={dataColumns({
              timeColumnsList: currentWeek.map((day) => format(day, 'dd-MM-yyyy')),
              tableData,
              starredProjects,
              userToSaveFor,
              toggleStarredProject,
              timeColumnTotals,
              calculateProjectTotalHours,
              totalWeekHours,
              handleUpdateData,
              handleUpdateWeeklyHours,
              sortedProjectsList,
              projectsList: projects,
              handleRemoveProject,
              handleAddProject,
              resetKey,
              setTimesheetModalData,
              companies,
              handleStarUpdate,
            })}
            stickyHeader={true}
            uniqueKey="uniqueId"
          />
        )}

        {!isSomething(timesheetModalData) && (
          <Table
            noDataMessage={'No time logged for this time frame.'}
            hasFooter={isSomething(tableData)}
            // data={[getInitialTableData(currentWeek), ...tableData]}
            data={[...tableData]}
            columns={dataColumns({
              tableData,
              starredProjects,
              userToSaveFor,
              toggleStarredProject,
              timeColumnsList: currentWeek.map((day) => format(day, 'dd-MM-yyyy')),
              timeColumnTotals,
              calculateProjectTotalHours,
              totalWeekHours,
              handleUpdateData,
              handleUpdateWeeklyHours,
              sortedProjectsList,
              projectsList: projects,
              handleRemoveProject,
              handleAddProject,
              resetKey,
              setTimesheetModalData,
              companies,
              handleStarUpdate,
            })}
            stickyHeader={true}
            uniqueKey="uniqueId"
          />
        )}
      </div>
      {/* <>
        {saveRequests > 0 && (
          <div className="save-requests">
            <Typography variant="body2" sx={{ fontWeight: "500" }}>
              Saving weekly hours... ({saveRequests})
            </Typography>
          </div>
        )}
      </> */}
      {isSomething(timesheetModalData) && (
        <TimesheetNotesModal
          timesheetModalData={timesheetModalData}
          onClose={() => setTimesheetModalData(null)}
          companies={companies}
          projectData={projectsList.find((p) => p.id === timesheetModalData.rowData.id)}
        />
      )}
    </TimeSheetStyled>
  );
};

export default TimeSheet;
